import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { cardiacTokens as T } from '../../../theme/cardiacTheme';
import {
  MAX_MEASUREMENT_CONDITIONS,
  MEASUREMENT_FILTER_FIELDS,
  MEASUREMENT_FILTER_FIELD_BY_KEY,
  MeasurementCondition,
  MeasurementFilterField,
  MeasurementOp,
  parseMeasurementConditions,
  toEnteredValue,
  toStoredValue,
} from '../measurementFilterFields';

/**
 * Measurement filter for the Report List: a toolbar button plus a chip per applied
 * condition, and a dialog to build the conditions (value → operator → number, all must
 * match). The dialog edits a draft; the list only refetches on Apply.
 */
interface Props {
  /** Applied conditions as the `measurementFilters` JSON string ('' = none). */
  value: string;
  onChange: (value: string) => void;
}

interface Row {
  id: number;
  key: string;
  op: MeasurementOp;
  value: string;
  min: string;
  max: string;
}

const OPS: { op: MeasurementOp; label: string; title: string }[] = [
  { op: 'gt', label: '>', title: 'Greater than' },
  { op: 'gte', label: '≥', title: 'At least' },
  { op: 'lt', label: '<', title: 'Less than' },
  { op: 'lte', label: '≤', title: 'At most' },
  { op: 'eq', label: '=', title: 'Equal to' },
  { op: 'between', label: 'Between', title: 'Between two values (inclusive)' },
];
const OP_LABEL = Object.fromEntries(OPS.map(o => [o.op, o.label])) as Record<MeasurementOp, string>;

let nextRowId = 1;
const emptyRow = (): Row => ({ id: nextRowId++, key: '', op: 'gt', value: '', min: '', max: '' });

const toRows = (raw: string): Row[] =>
  parseMeasurementConditions(raw).map(c => ({
    id: nextRowId++,
    key: c.key,
    op: c.op,
    value: c.value === undefined ? '' : String(toEnteredValue(c.key, c.value)),
    min: c.min === undefined ? '' : String(toEnteredValue(c.key, c.min)),
    max: c.max === undefined ? '' : String(toEnteredValue(c.key, c.max)),
  }));

const isNumber = (s: string) => s.trim() !== '' && Number.isFinite(Number(s));
const isBlank = (r: Row) => !r.key && !r.value && !r.min && !r.max;
const isComplete = (r: Row) =>
  !!r.key && (r.op === 'between' ? isNumber(r.min) && isNumber(r.max) : isNumber(r.value));

function toCondition(r: Row): MeasurementCondition {
  if (r.op === 'between') {
    return {
      key: r.key,
      op: r.op,
      min: toStoredValue(r.key, Number(r.min)),
      max: toStoredValue(r.key, Number(r.max)),
    };
  }
  return { key: r.key, op: r.op, value: toStoredValue(r.key, Number(r.value)) };
}

/** "EF Biplane < 50 %" / "TR Vmax 2.5–3.5 m/s" for a chip. */
function describe(c: MeasurementCondition): string {
  const f = MEASUREMENT_FILTER_FIELD_BY_KEY[c.key];
  const unit = f?.unit ? ` ${f.unit}` : '';
  if (c.op === 'between') {
    return `${f?.label} ${toEnteredValue(c.key, c.min ?? 0)}–${toEnteredValue(c.key, c.max ?? 0)}${unit}`;
  }
  return `${f?.label} ${OP_LABEL[c.op]} ${toEnteredValue(c.key, c.value ?? 0)}${unit}`;
}

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    fontSize: 13.5,
    color: T.ink,
    bgcolor: T.bg,
    borderRadius: '10px',
    '& fieldset': { borderColor: T.line },
    '&:hover fieldset': { borderColor: T.lineStrong },
    '&.Mui-focused fieldset': { borderColor: T.teal },
    '&.Mui-error fieldset': { borderColor: T.amber },
  },
  '& .MuiInputAdornment-root .MuiTypography-root': { fontSize: 12.5, color: T.ink3 },
  '& .MuiSvgIcon-root': { color: T.ink2 },
  '& input::placeholder': { color: T.ink3, opacity: 1 },
};

const popupPaperSx = {
  bgcolor: '#0b1b2b',
  backgroundImage: 'none',
  color: T.ink,
  border: `1px solid ${T.line}`,
  boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
  '& .MuiAutocomplete-groupLabel': {
    bgcolor: '#0b1b2b',
    color: T.teal,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    lineHeight: '30px',
  },
  '& .MuiAutocomplete-option': { fontSize: 13.5 },
  '& .MuiAutocomplete-option[aria-selected="true"]': {
    bgcolor: 'rgba(45,212,191,0.14) !important',
  },
  '& .MuiAutocomplete-noOptions': { color: T.ink3, fontSize: 13 },
};

export default function MeasurementFilter({ value, onChange }: Props) {
  const applied = useMemo(() => parseMeasurementConditions(value), [value]);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const isActive = applied.length > 0;

  const openDialog = () => {
    const current = toRows(value);
    setRows(current.length ? current : [emptyRow()]);
    setShowErrors(false);
    setOpen(true);
  };

  const update = (id: number, patch: Partial<Row>) =>
    setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const remove = (id: number) => setRows(rs => rs.filter(r => r.id !== id));
  const add = () =>
    setRows(rs => (rs.length >= MAX_MEASUREMENT_CONDITIONS ? rs : [...rs, emptyRow()]));

  // Untouched rows are ignored; a half-filled row blocks Apply and is highlighted.
  const filled = rows.filter(r => !isBlank(r));
  const canApply = filled.every(isComplete);
  const usedKeys = new Set(rows.map(r => r.key).filter(Boolean));

  const apply = () => {
    if (!canApply) {
      setShowErrors(true);
      return;
    }
    onChange(filled.length ? JSON.stringify(filled.map(toCondition)) : '');
    setOpen(false);
  };
  const clearAll = () => {
    onChange('');
    setOpen(false);
  };
  const removeApplied = (index: number) => {
    const next = applied.filter((_, i) => i !== index);
    onChange(next.length ? JSON.stringify(next) : '');
  };

  return (
    <>
      <Button
        onClick={openDialog}
        startIcon={<MonitorHeartOutlinedIcon sx={{ fontSize: 16 }} />}
        // Same look as the neighbouring "More filters" button (.csl-more).
        sx={{
          px: '14px',
          py: '8px',
          minHeight: 0,
          lineHeight: 1.4,
          borderRadius: '10px',
          textTransform: 'none',
          fontSize: 13,
          fontWeight: 600,
          whiteSpace: 'nowrap',
          color: isActive ? T.teal : T.ink2,
          border: `1px solid ${isActive ? T.teal : T.line}`,
          bgcolor: isActive ? 'rgba(45,212,191,0.08)' : 'transparent',
          '& .MuiButton-startIcon': { mr: '7px' },
          '&:hover': {
            color: T.teal,
            borderColor: 'rgba(45,212,191,0.5)',
            bgcolor: isActive ? 'rgba(45,212,191,0.12)' : 'transparent',
          },
        }}
      >
        Measurements{isActive ? ` · ${applied.length}` : ''}
      </Button>

      {applied.map((c, i) => (
        <Chip
          key={`${c.key}-${i}`}
          label={describe(c)}
          onClick={openDialog}
          onDelete={() => removeApplied(i)}
          deleteIcon={<CloseIcon />}
          sx={{
            height: 28,
            fontSize: 12,
            fontWeight: 600,
            color: '#5eead4',
            bgcolor: 'rgba(45,212,191,0.10)',
            border: '1px solid rgba(45,212,191,0.35)',
            '& .MuiChip-deleteIcon': { fontSize: 15, color: 'rgba(94,234,212,0.7)' },
            '& .MuiChip-deleteIcon:hover': { color: '#5eead4' },
            '&:hover': { bgcolor: 'rgba(45,212,191,0.16)' },
          }}
        />
      ))}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: T.surface,
            backgroundImage: 'none',
            color: T.ink,
            border: `1px solid ${T.line}`,
            borderRadius: '16px',
            boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          },
        }}
      >
        <DialogTitle
          sx={{ pb: 0.5, pr: 6, fontFamily: theme => theme.typography.body1.fontFamily }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 700, color: T.ink }}>
            Filter by measurements
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: T.ink3, mt: 0.25 }}>
            Shows reports whose measurement table matches every condition.
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', top: 12, right: 12, color: T.ink3 }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{ pt: '14px !important', display: 'flex', flexDirection: 'column', gap: 1.25 }}
        >
          {rows.map((row, index) => {
            const field = MEASUREMENT_FILTER_FIELD_BY_KEY[row.key] || null;
            const invalid = showErrors && !isBlank(row) && !isComplete(row);
            const unit = field?.unit ? (
              <InputAdornment position="end">{field.unit}</InputAdornment>
            ) : undefined;
            const numberInput = (
              val: string,
              key: 'value' | 'min' | 'max',
              placeholder: string
            ) => (
              <TextField
                type="number"
                size="small"
                placeholder={placeholder}
                value={val}
                error={invalid && !isNumber(val)}
                onChange={e => update(row.id, { [key]: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    apply();
                  }
                }}
                InputProps={{ endAdornment: unit }}
                inputProps={{ step: 'any', 'aria-label': placeholder }}
                sx={{ ...fieldSx, flex: 1, minWidth: 110 }}
              />
            );

            return (
              <Box
                key={row.id}
                sx={{
                  p: 1.5,
                  borderRadius: '12px',
                  bgcolor: T.surface2,
                  border: `1px solid ${invalid ? 'rgba(247,163,90,0.55)' : T.line}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      width: 20,
                      fontSize: 12,
                      fontWeight: 700,
                      color: T.ink3,
                      textAlign: 'center',
                    }}
                  >
                    {index + 1}
                  </Typography>
                  <Autocomplete<MeasurementFilterField>
                    size="small"
                    fullWidth
                    options={MEASUREMENT_FILTER_FIELDS}
                    groupBy={f => f.group}
                    getOptionLabel={f => f.label}
                    getOptionDisabled={f => usedKeys.has(f.key) && f.key !== row.key}
                    isOptionEqualToValue={(a, b) => a.key === b.key}
                    value={field}
                    onChange={(_, f) => update(row.id, { key: f?.key || '' })}
                    renderOption={(props, f) => (
                      <li
                        {...props}
                        key={f.key}
                      >
                        <Box
                          sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
                        >
                          <span>{f.label}</span>
                          <Typography
                            component="span"
                            sx={{ fontSize: 12, color: T.ink3 }}
                          >
                            {f.unit}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Choose a measurement (e.g. EF Biplane)"
                        error={invalid && !row.key}
                      />
                    )}
                    slotProps={{ paper: { sx: popupPaperSx } }}
                    sx={fieldSx}
                  />
                  <Tooltip title="Remove condition">
                    <IconButton
                      size="small"
                      aria-label="Remove condition"
                      onClick={() => remove(row.id)}
                      sx={{ color: T.ink3, '&:hover': { color: T.red } }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    pl: '28px',
                    flexWrap: 'wrap',
                  }}
                >
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={row.op}
                    onChange={(_, op) => op && update(row.id, { op })}
                    sx={{
                      '& .MuiToggleButton-root': {
                        height: 36,
                        minWidth: 36,
                        px: 1.1,
                        fontSize: 13.5,
                        fontWeight: 700,
                        textTransform: 'none',
                        color: T.ink2,
                        borderColor: T.line,
                        bgcolor: T.bg,
                      },
                      '& .MuiToggleButton-root.Mui-selected': {
                        color: '#04221d',
                        bgcolor: T.teal,
                        borderColor: T.teal,
                      },
                      '& .MuiToggleButton-root.Mui-selected:hover': { bgcolor: '#5eead4' },
                    }}
                  >
                    {/* Plain `title`: a Tooltip wrapper would stop the group passing
                        `selected` to the button. */}
                    {OPS.map(o => (
                      <ToggleButton
                        key={o.op}
                        value={o.op}
                        title={o.title}
                        aria-label={o.title}
                      >
                        {o.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {row.op === 'between' ? (
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 240 }}
                    >
                      {numberInput(row.min, 'min', 'From')}
                      <Typography sx={{ fontSize: 12.5, color: T.ink3 }}>and</Typography>
                      {numberInput(row.max, 'max', 'To')}
                    </Box>
                  ) : (
                    numberInput(row.value, 'value', 'Value')
                  )}
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 17 }} />}
              onClick={add}
              disabled={rows.length >= MAX_MEASUREMENT_CONDITIONS}
              sx={{
                textTransform: 'none',
                fontSize: 13,
                fontWeight: 600,
                color: T.teal,
                '&.Mui-disabled': { color: T.ink3 },
              }}
            >
              Add condition
            </Button>
            <Typography sx={{ fontSize: 12, color: T.ink3 }}>
              {rows.length} of {MAX_MEASUREMENT_CONDITIONS}
            </Typography>
          </Box>

          {showErrors && !canApply && (
            <Typography sx={{ fontSize: 12.5, color: T.amber }}>
              Finish the highlighted condition: choose a measurement and enter a number.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          {isActive && (
            <Button
              onClick={clearAll}
              sx={{ textTransform: 'none', fontSize: 13, color: T.red, mr: 'auto' }}
            >
              Clear all
            </Button>
          )}
          <Button
            onClick={() => setOpen(false)}
            sx={{ textTransform: 'none', fontSize: 13, color: T.ink2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={apply}
            sx={{
              textTransform: 'none',
              fontSize: 13,
              fontWeight: 700,
              px: 2.5,
              borderRadius: '10px',
              bgcolor: T.teal,
              color: '#04221d',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#5eead4', boxShadow: 'none' },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
